/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {
  getTotalPages,
  getPdfTitleAJS,
  getFileName,
  getIsEditingAllowed,
  isDocumentEncrypted,
  isFilePasswordProtected,
  getHasAcroFormFields,
  getIsXFA,
  getIsPortfolio,
  getDocWillSaveActions,
  getDocDidSaveActions,
  getHasFASFields
} from "selectors/documentSelectors";
import { ajsMetadataDonePromise } from "utils/DocumentViewUtils";
import { initialPageLoadedPromise } from "utils/DocumentViewUtils";
import {
  getPageOrigHeight,
  getPageOrigWidth,
  getPageRotation,
  getPageLoadStatus,
  getPageBoxes,
  isValidPageIndex,
  getPageTransformMatrix
} from "selectors/pageSelectors";
import { getPageZoomLevel } from "selectors/zoom";
import { loadStatus, PDF_ACTIONS } from "constants/enums";
import { isReadOnlyModeEnabled, getSupportPDFJavaScriptPromise } from "selectors/appSelectors";
import { isModifyingContentAllowed, hasSecurityBitSet, isCommentingAllowed } from "utils/DocPermissionsUtils";
import { getShouldEnableEncryptedWrite } from "selectors/overlayDropinSelectors";
import { failureType } from "dc-preview-utils/src/Constants/enums";
import { isNumber } from "utils/APIUtils";
import docAPI from "api";
import { getFontListOfPage, getFileSize, getDocInfo } from "selectors/documentSelectors";
import { getHasDigSigFromMetadata } from "selectors/digSigSelectors";
import {
  GET_PAGE_FONT_LIST_PRIORITY,
  GET_PAGES_SCAN_STATUS_PRIORITY,
  GET_IS_SCANNED_DOCUMENT,
  GET_ASSET_VERSION,
  WAIT_FOR_DOC_SAVE_COMPLETION
} from "constants/constants";
import { callbackService } from "publicApis/controller/callbackService";

function _getIsDocumentUnEditable(stores) {
  return (
    isFilePasswordProtected(stores) ||
    isDocumentEncrypted(stores) ||
    (!isModifyingContentAllowed() && isReadOnlyModeEnabled(stores)) ||
    hasSecurityBitSet() ||
    getIsPortfolio(stores) ||
    getHasDigSigFromMetadata(stores)
  );
}

function _getEditFailureType(stores) {
  let editingFailureType;
  if (isFilePasswordProtected(stores)) editingFailureType = failureType.PASSWORD_PROTECTED;
  else if (isDocumentEncrypted(stores)) editingFailureType = failureType.ENCRYPTED_DOCUMENT;
  else if (!isModifyingContentAllowed() && isReadOnlyModeEnabled(stores))
    editingFailureType = failureType.READ_ONLY_DOCUMENT;
  else if (getHasDigSigFromMetadata(stores)) editingFailureType = failureType.DIGITALLY_SIGNED_DOCUMENT;
  else if (hasSecurityBitSet()) editingFailureType = failureType.POLICY_PROTECTED_DOCUMENT;
  else if (getIsPortfolio(stores)) editingFailureType = failureType.PORT_FOLIO_FILE;
  // doubt if we need to stop for this
  else editingFailureType = failureType.POLICY_PROTECTED_DOCUMENT;
  return editingFailureType;
}

const _hasJSActionOnSave = stores => {
  const callback = action => action.actionType === PDF_ACTIONS.JAVASCRIPT;
  let jsActionsLength = getDocWillSaveActions(stores).filter(callback).length;
  if (jsActionsLength) return true;

  jsActionsLength = getDocDidSaveActions(stores).filter(callback).length;
  return !!jsActionsLength;
};

export const getDocumentInfoApis = stores => {
  function getMetadata() {
    return new Promise((resolve, reject) => {
      ajsMetadataDonePromise(stores)
        .then(() => {
          const metadata = {
            numPages: getTotalPages(stores),
            pdfTitle: getPdfTitleAJS(stores),
            fileName: getFileName(stores),
            isXFA: getIsXFA(stores),
            hasAcroFormFields: getHasAcroFormFields(stores),
            hasDigitalSignatures: getHasDigSigFromMetadata(stores),
            fileSize: getFileSize(stores),
            hasJSActionOnSave: _hasJSActionOnSave(stores),
            docInfo: getDocInfo(stores),
            isPortfolio: getIsPortfolio(stores),
            hasFASFields: getHasFASFields(stores)
          };
          resolve(metadata);
        })
        .catch(reject);
    });
  }

  function getPageAttributes(pageNum) {
    return new Promise((resolve, reject) => {
      ajsMetadataDonePromise(stores)
        .then(() => {
          const pageIndex = pageNum - 1;
          if (!isNumber(pageIndex) || !isValidPageIndex(stores, pageIndex)) {
            reject("Page number is not valid");
          }
          let pageLoadStatus = getPageLoadStatus(stores, pageIndex);
          if (
            pageLoadStatus === loadStatus.BEING_PRELOADED ||
            pageLoadStatus === loadStatus.PINCH_ZOOM_IN_PROGRESS ||
            pageLoadStatus === loadStatus.BEING_LOADED
          ) {
            pageLoadStatus = "BEING_LOADED";
          } else if (pageLoadStatus === loadStatus.NOT_LOADED) {
            pageLoadStatus = "NOT_LOADED";
          } else pageLoadStatus = "LOADED";
          let pageBoxes = getPageBoxes(stores, pageIndex);
          pageBoxes = JSON.parse(JSON.stringify(pageBoxes));
          const response = {
            tMatrix: getPageTransformMatrix(stores, pageIndex),
            height: getPageOrigHeight(stores, pageIndex),
            width: getPageOrigWidth(stores, pageIndex),
            zoom: getPageZoomLevel(stores, pageIndex),
            rotation: getPageRotation(stores, pageIndex),
            loadStatus: pageLoadStatus,
            boxes: {
              CropBox: pageBoxes.cropBox,
              mediaBox: pageBoxes.mediaBox
            }
          };
          resolve(response);
        })
        .catch(reject);
    });
  }

  function getNumPages() {
    return ajsMetadataDonePromise(stores).then(() => getTotalPages(stores));
  }

  function getPageFontList(pageNum, priority = GET_PAGE_FONT_LIST_PRIORITY) {
    return new Promise((resolve, reject) => {
      let pageIndex = pageNum - 1;
      if (!isNumber(pageIndex) || !isValidPageIndex(stores, pageIndex)) {
        reject("Page number is not valid");
      }
      let fontList = getFontListOfPage(stores, pageIndex);
      if (fontList) {
        resolve({ fontList });
      } else {
        docAPI
          .getPageFontList({ pageNum, priority })
          .then(fontList => {
            resolve(fontList);
          })
          .catch(reject);
      }
    });
  }

  function isEditingAllowed() {
    return ajsMetadataDonePromise(stores).then(() => {
      const isEditingAllowed = getIsEditingAllowed(stores);
      if (isEditingAllowed && !_getIsDocumentUnEditable(stores)) {
        return Promise.resolve(failureType.NONE);
      }
      return Promise.reject(_getEditFailureType(stores));
    });
  }

  function isFillAndSignAllowed() {
    return ajsMetadataDonePromise(stores).then(() => {
      if (isFilePasswordProtected(stores) && !getShouldEnableEncryptedWrite(stores)) return Promise.reject();
      const isFillAndSignAllowed = isModifyingContentAllowed() && !isReadOnlyModeEnabled(stores);
      if (isFillAndSignAllowed && !getIsPortfolio(stores)) {
        return Promise.resolve();
      }
      return Promise.reject();
    });
  }

  function getBookmarks() {
    return new Promise((resolve, reject) => {
      initialPageLoadedPromise(stores)
        .then(() => {
          stores.bookmarkStore
            .getBookmarksFromDocAPI()
            .then(bookmarks => {
              resolve(bookmarks);
            })
            .catch(reject);
        })
        .catch(reject);
    });
  }

  function getAttachments() {
    return new Promise((resolve, reject) => {
      initialPageLoadedPromise(stores)
        .then(() => {
          stores.lhpStore.attachmentsModel
            .getAttachmentsFromDocAPI()
            .then(attachments => {
              resolve(attachments);
            })
            .catch(reject);
        })
        .catch(reject);
    });
  }

  function getAttachmentBuffer(attachmentName) {
    return new Promise((resolve, reject) => {
      if (attachmentName) {
        initialPageLoadedPromise(stores)
          .then(() => {
            docAPI
              .fetchAttachmentFile(attachmentName)
              .then(fetchAttachmentFileResponse => {
                if (
                  fetchAttachmentFileResponse &&
                  fetchAttachmentFileResponse.data &&
                  fetchAttachmentFileResponse.data.buffer &&
                  fetchAttachmentFileResponse.data.buffer.length
                ) {
                  resolve({
                    buffer: fetchAttachmentFileResponse.data.buffer
                  });
                } else {
                  reject();
                }
              })
              .catch(reject);
          })
          .catch(reject);
      } else {
        reject();
      }
    });
  }

  function getPageThumbnail(pageNum, thumbnailRect) {
    return new Promise((resolve, reject) => {
      initialPageLoadedPromise(stores)
        .then(() => {
          if (
            pageNum >= 1 &&
            pageNum <= getTotalPages(stores) &&
            thumbnailRect &&
            thumbnailRect.height &&
            thumbnailRect.width
          ) {
            docAPI
              .getPageThumbnail(pageNum, thumbnailRect)
              .ajs.then(response => {
                if (response && response.data) {
                  resolve({
                    buffer: response.data.buffer,
                    mimeType: response.data.mimeType
                  });
                } else {
                  reject();
                }
              })
              .catch(reject);
          } else {
            reject();
          }
        })
        .catch(reject);
    });
  }

  function setFileName(fileName) {
    stores.documentStore.setFileName(fileName);
  }

  const getPagesScanStatus = (startPageIndex, endPageIndex, ocrType, priority = GET_PAGES_SCAN_STATUS_PRIORITY) =>
    new Promise((resolve, reject) => {
      docAPI
        .getPagesScanStatus(startPageIndex, endPageIndex, ocrType, priority)
        .then(data => {
          resolve(data);
        })
        .catch(reject);
    });

  const getIsScannedDocument = (priority = GET_IS_SCANNED_DOCUMENT) => docAPI.getIsScannedDocument(priority);

  const getDocumentParts = ({ includeSignData = false }) => docAPI.getDocumentParts({ includeSignData });

  const setDocumentParts = dPartInfo => docAPI.setDocumentParts(dPartInfo);

  const updateDocInfo = data => docAPI.updateDocInfo(data);

  const isCommentingEnabled = () =>
    ajsMetadataDonePromise(stores).then(() => (isCommentingAllowed() ? Promise.resolve() : Promise.reject()));

  const isFileProtected = () =>
    ajsMetadataDonePromise(stores).then(() =>
      isFilePasswordProtected(stores) || isDocumentEncrypted(stores) ? Promise.resolve(true) : Promise.resolve(false)
    );

  const isPDFJavaScriptEnabled = () => getSupportPDFJavaScriptPromise(stores);

  const getAssetVersion = () => {
    if (callbackService.hasCallback(GET_ASSET_VERSION)) {
      return callbackService.getCallback(GET_ASSET_VERSION)();
    }
    return Promise.reject(new Error(`No callback registered for ${GET_ASSET_VERSION}`));
  };

  const waitForPendingChanges = () => {
    if (callbackService.hasCallback(WAIT_FOR_DOC_SAVE_COMPLETION)) {
      return callbackService.getCallback(WAIT_FOR_DOC_SAVE_COMPLETION)();
    }
    return Promise.reject(new Error(`No callback registered for ${WAIT_FOR_DOC_SAVE_COMPLETION}`));
  };

  return {
    getMetadata,
    getNumPages,
    getPageAttributes,
    getPageFontList,
    isEditingAllowed,
    getBookmarks,
    getAttachments,
    getAttachmentBuffer,
    getPageThumbnail,
    getPagesScanStatus,
    getIsScannedDocument,
    getDocumentParts,
    setDocumentParts,
    setFileName,
    isCommentingAllowed: isCommentingEnabled,
    isFileProtected,
    isFillAndSignAllowed,
    isPDFJavaScriptEnabled,
    getAssetVersion,
    waitForPendingChanges,
    updateDocInfo
  };
};
